import React from 'react';
import { Link } from 'react-router';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../../components/LanguageSwitcher';
import { ProductLine } from '../../../types/auth.types';

interface NavLink {
  label: string;
  href: string;
  isExternal?: boolean;
}

interface LandingHeaderProps {
  navLinks?: NavLink[];
  product?: ProductLine;
  showProductLinks?: boolean;
}

/**
 * Shared landing page header component.
 * Uses the main design system for consistent styling across all landing pages.
 */
export const LandingHeader: React.FC<LandingHeaderProps> = ({
  navLinks,
  product,
  showProductLinks = false,
}) => {
  const { t } = useTranslation(['landing', 'common']);

  const signUpUrl = product ? `/signup?product=${product}` : '/signup';

  // Default nav links
  const defaultLinks: NavLink[] = [
    { label: t('landing:navigation.product'), href: '#product' },
    { label: t('landing:navigation.useCases'), href: '#use-cases' },
    { label: t('landing:navigation.pricing'), href: '#pricing' },
    { label: t('landing:navigation.docs'), href: 'https://docs.callcov.com', isExternal: true },
  ];

  // Product-specific links (for product landing pages)
  const productLinks: NavLink[] = [
    { label: t('landing:navigation.features'), href: '#features' },
    { label: t('landing:navigation.pricing'), href: '#pricing' },
    ...(product !== 'comply' ? [{ label: t('landing:navigation.compliance'), href: '/compliance' }] : []),
    ...(product !== 'quality' ? [{ label: t('landing:navigation.quality'), href: '/quality' }] : []),
    { label: t('landing:navigation.docs'), href: 'https://docs.callcov.com', isExternal: true },
  ];

  const links = navLinks || (showProductLinks ? productLinks : defaultLinks);

  return (
    <Disclosure as="header" className="absolute inset-x-0 top-0 z-50">
      {({ open }) => (
        <>
          <nav className="flex items-center justify-between p-6 lg:px-8" aria-label="Global">
            <div className="flex lg:flex-1">
              <Link to="/" className="-m-1.5 p-1.5 group">
                <span className="text-2xl font-display font-bold gradient-text transition-all duration-300">
                  {t('common:app.name')}
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex lg:gap-x-8">
              {links.map((link) =>
                link.isExternal ? (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-semibold leading-6 text-neutral-900 hover:text-primary-600 transition-colors"
                  >
                    {link.label}
                  </a>
                ) : link.href.startsWith('#') ? (
                  <a
                    key={link.href}
                    href={link.href}
                    className="text-sm font-semibold leading-6 text-neutral-900 hover:text-primary-600 transition-colors"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="text-sm font-semibold leading-6 text-neutral-900 hover:text-primary-600 transition-colors"
                  >
                    {link.label}
                  </Link>
                )
              )}
            </div>

            {/* Desktop Actions */}
            <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:gap-x-4 lg:items-center">
              <LanguageSwitcher />
              <Link
                to="/signin"
                className="text-sm font-semibold leading-6 text-neutral-900 hover:text-primary-600 transition-colors px-3 py-2"
              >
                {t('landing:navigation.signIn')} <span aria-hidden="true">&rarr;</span>
              </Link>
              <Link to={signUpUrl} className="btn btn-primary text-sm px-5 py-2.5 rounded-lg">
                {t('landing:navigation.getStarted')}
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="flex lg:hidden gap-x-3 items-center">
              <LanguageSwitcher />
              <DisclosureButton className="inline-flex items-center justify-center rounded-md p-2.5 text-neutral-700 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500">
                <span className="sr-only">{open ? t('landing:navigation.closeMenu') : t('landing:navigation.openMenu')}</span>
                {open ? (
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                )}
              </DisclosureButton>
            </div>
          </nav>

          {/* Mobile menu panel */}
          <DisclosurePanel className="lg:hidden">
            <div className="space-y-1 px-4 pb-3 pt-2 bg-white shadow-lg border-t border-neutral-200">
              {links.map((link) =>
                link.isExternal ? (
                  <DisclosureButton
                    key={link.href}
                    as="a"
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-md px-3 py-2 text-base font-medium text-neutral-700 hover:bg-neutral-50 hover:text-primary-600 transition-colors"
                  >
                    {link.label}
                  </DisclosureButton>
                ) : link.href.startsWith('#') ? (
                  <DisclosureButton
                    key={link.href}
                    as="a"
                    href={link.href}
                    className="block rounded-md px-3 py-2 text-base font-medium text-neutral-700 hover:bg-neutral-50 hover:text-primary-600 transition-colors"
                  >
                    {link.label}
                  </DisclosureButton>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="block rounded-md px-3 py-2 text-base font-medium text-neutral-700 hover:bg-neutral-50 hover:text-primary-600 transition-colors"
                  >
                    {link.label}
                  </Link>
                )
              )}

              {/* Mobile auth buttons */}
              <div className="mt-4 space-y-2 border-t border-neutral-200 pt-4">
                <Link
                  to="/signin"
                  className="block rounded-md px-3 py-2 text-base font-medium text-neutral-700 hover:bg-neutral-50 hover:text-primary-600 transition-colors"
                >
                  {t('landing:navigation.signIn')}
                </Link>
                <Link
                  to={signUpUrl}
                  className="block rounded-md px-3 py-2 text-base font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors text-center"
                >
                  {t('landing:navigation.getStarted')}
                </Link>
              </div>
            </div>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  );
};

export default LandingHeader;
